import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Settings2, Sparkles, Receipt } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import { billingApi } from '../../lib/billing';

// Re-fetched on mount and once every 60 s. Other modules can dispatch a global
// 'billing:refresh' event to force an immediate refresh after payment success.
export function QuotaPill() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    let timer;
    const fetch = async () => {
      try {
        const d = await billingApi.me();
        if (!active) return;
        setData(d);
      } catch (_e) {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetch();
    timer = setInterval(fetch, 60_000);
    const onRefresh = () => fetch();
    window.addEventListener('billing:refresh', onRefresh);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener('billing:refresh', onRefresh);
    };
  }, []);

  if (loading) {
    return (
      <span className="inline-block h-7 w-44 rounded-full bg-secondary animate-pulse" data-testid="app-shell-quota-pill-skeleton" />
    );
  }
  if (!data) return null;

  // Admin bypass — the whole quota system is disabled for admins. Show a
  // compact "Admin - Unlimited" pill (no upsell CTA, no usage meters).
  if (data.entitlement_key === 'admin_unlimited') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-medium text-primary tracking-[0.02em]"
        data-testid="app-shell-admin-pill"
        title="Admin session — no quota limits apply"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
        Admin &middot; Unlimited
      </span>
    );
  }

  const isNoPlan = data.entitlement_key === 'none';
  const isExpired = data.entitlement_key === 'expired';
  if (isNoPlan || isExpired) {
    return (
      <Button
        size="sm"
        onClick={() => navigate('/pricing')}
        className="font-mono-edge text-xs tracking-[0.06em]"
        data-testid="app-shell-upgrade-button"
      >
        UPGRADE <ArrowUpRight size={12} className="ml-1" />
      </Button>
    );
  }

  const usage = data.usage || {};
  const posts = data.posts_limit
    ? `${usage.posts_used || 0}/${data.posts_limit} posts`
    : null;
  const carousels = data.carousels_limit
    ? `${usage.carousels_used || 0}/${data.carousels_limit} carousels`
    : null;
  const audits = data.audits_limit
    ? `${usage.audits_used || 0}/${data.audits_limit} audits`
    : null;
  const days = typeof data.days_remaining === 'number' ? `${data.days_remaining}d left` : null;

  // Determine threshold severity from highest-utilised metric
  let severity = 'ok';
  const pcts = [];
  if (data.posts_limit) pcts.push((usage.posts_used || 0) / data.posts_limit);
  if (data.carousels_limit) pcts.push((usage.carousels_used || 0) / data.carousels_limit);
  if (data.audits_limit) pcts.push((usage.audits_used || 0) / data.audits_limit);
  const top = pcts.length ? Math.max(...pcts) : 0;
  if (top >= 0.9) severity = 'danger';
  else if (top >= 0.7) severity = 'warn';

  const dotColor = severity === 'danger' ? 'bg-[hsl(var(--destructive))]' : severity === 'warn' ? 'bg-[hsl(var(--warning))]' : 'bg-[hsl(var(--success))]';

  const segments = [audits, posts, carousels, days].filter(Boolean);

  // ------- Plan-aware copy for the popover -------
  const planType = data.plan_type || 'none';
  const planName = (() => {
    if (planType === 'gold') return 'Executive Thought Leadership Plan';
    if (planType === 'one_time') return 'Profile Success Plan';
    if (planType === 'comp') return 'Complimentary Access';
    return data.label || 'No active plan';
  })();
  const isUpgradeable = planType === 'one_time' && data.status === 'active';
  const planBadgeTone = (() => {
    if (planType === 'gold') return 'bg-primary/10 text-primary border-primary/20';
    if (planType === 'comp') return 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20';
    return 'bg-secondary text-foreground/80 border-border';
  })();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1.5 font-mono-edge text-xs hover:bg-secondary/70 focus-visible:ring-2 focus-visible:ring-ring transition-colors',
            severity === 'danger' && 'ring-1 ring-[hsl(var(--destructive))]/40',
            severity === 'warn' && 'ring-1 ring-[hsl(var(--warning))]/40',
          )}
          aria-label={`Plan: ${planName}. Click to manage`}
          data-testid="app-shell-quota-pill"
        >
          <span className={cn('h-1.5 w-1.5 rounded-full', dotColor)} aria-hidden />
          <span className="hidden md:inline">{segments.join(' \u00b7 ')}</span>
          <span className="md:hidden">{data.label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[22rem] p-0 overflow-hidden"
        data-testid="app-shell-quota-pill-popover"
      >
        {/* Plan header */}
        <div className="px-4 pt-4 pb-3 bg-gradient-to-br from-[hsl(var(--primary-soft))]/40 via-card to-card border-b border-border">
          <p className="text-[10px] font-mono-edge tracking-[0.18em] text-muted-foreground">
            ACTIVE PLAN
          </p>
          <div className="mt-1 flex items-start justify-between gap-2">
            <p className="font-semibold text-sm leading-snug" data-testid="quota-popover-plan-name">
              {planName}
            </p>
            <Badge
              variant="outline"
              className={cn('font-mono-edge text-[10px] tracking-[0.14em] shrink-0', planBadgeTone)}
              data-testid="quota-popover-plan-badge"
            >
              {(data.status || 'inactive').toUpperCase()}
            </Badge>
          </div>
          {days && (
            <p className="text-xs text-muted-foreground mt-1">{days}</p>
          )}
        </div>

        {/* Usage breakdown */}
        <div className="px-4 py-3 space-y-1.5 text-xs">
          {audits && (
            <div className="flex items-center justify-between" data-testid="quota-popover-row-audits">
              <span className="text-muted-foreground">Audits</span>
              <span className="font-medium font-mono-edge">{audits.replace(' audits', '')}</span>
            </div>
          )}
          {posts && (
            <div className="flex items-center justify-between" data-testid="quota-popover-row-posts">
              <span className="text-muted-foreground">Posts</span>
              <span className="font-medium font-mono-edge">{posts.replace(' posts', '')}</span>
            </div>
          )}
          {carousels && (
            <div className="flex items-center justify-between" data-testid="quota-popover-row-carousels">
              <span className="text-muted-foreground">Carousels</span>
              <span className="font-medium font-mono-edge">{carousels.replace(' carousels', '')}</span>
            </div>
          )}
          {!audits && !posts && !carousels && (
            <p className="text-muted-foreground italic">No quota tracked on this plan.</p>
          )}
        </div>

        {/* CTAs */}
        <div className="px-4 pb-4 pt-1 space-y-2">
          {isUpgradeable && (
            <Button
              onClick={() => navigate('/pricing')}
              className="w-full justify-center rounded-xl text-[13px] font-medium"
              data-testid="quota-popover-upgrade-cta"
            >
              <Sparkles size={13} className="mr-1.5 shrink-0" />
              <span className="truncate">Upgrade to Thought Leadership</span>
            </Button>
          )}
          <Button
            variant={isUpgradeable ? 'outline' : 'default'}
            onClick={() => navigate('/billing')}
            className="w-full justify-center rounded-xl"
            data-testid="quota-popover-manage-cta"
          >
            <Settings2 size={13} className="mr-1.5" />
            Manage plan
          </Button>
          <button
            type="button"
            onClick={() => navigate('/billing')}
            className="w-full flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground underline-offset-4 hover:underline pt-1"
            data-testid="quota-popover-receipts-link"
          >
            <Receipt size={11} /> View invoices &amp; usage
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default QuotaPill;
